"use client";

import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ArrowLeft, Grid3x3, List, Minus, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthStore } from "@/stores/authStore";
import { api } from "@/lib/axios";
import { apiBaseURL } from "@/lib/api-config";
import { cn } from "@/lib/utils";
import { resolveInvoicePortalUrl } from "@/lib/invoice-portal";
import { POS_HOLDS_STORAGE_KEY } from "@/lib/client-storage-keys";
import { useBranches } from "@/hooks/api/useBranches";
import { useBranchesEnabled } from "@/hooks/api/useBranchesEnabled";
import { useCategoryTree } from "@/hooks/api/useCategoryTree";
import { useProducts } from "@/hooks/api/useProducts";
import type { ProductRow } from "@/lib/types/product";
import type { CategoryNode } from "@/lib/types/product";
import { calculateGst } from "@/lib/pos/gst-preview";
import {
  resolveLineOffer,
  effectiveLineDiscPct,
  cartBillQtyTotal,
  type ApplicableOffer,
  type LineOfferResolution,
} from "@/lib/pos/offers";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  PaymentModal,
  type PaymentRow,
  openSalePdf,
  openSaleThermal,
  downloadSalePdf,
  buildWhatsAppLink,
} from "@/components/pos/PaymentModal";

type CustomerRow = {
  id: number;
  name: string;
  phone: string;
  email?: string | null;
  gstin?: string | null;
  address?: string | null;
};

type CartLine = {
  product: ProductRow;
  qty: number;
  rate: number;
  discPct: number;
};

type HeldBill = {
  id: string;
  savedAt: number;
  cart: CartLine[];
  customer: CustomerRow | null;
  cartDiscPct: number;
};

const MAX_HOLDS = 5;

function flattenCategories(nodes: CategoryNode[] | undefined): { id: number; name: string }[] {
  if (!nodes?.length) {
    return [];
  }
  const acc: { id: number; name: string }[] = [];
  const walk = (n: CategoryNode) => {
    acc.push({ id: n.id, name: n.name });
    n.children?.forEach(walk);
  };
  nodes.forEach(walk);

  return acc;
}

function playScanBeep() {
  try {
    void new Audio("/beep.mp3").play();
  } catch {
    try {
      const ctx = new AudioContext();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.frequency.value = 880;
      g.gain.value = 0.08;
      o.start();
      setTimeout(() => {
        o.stop();
        void ctx.close();
      }, 80);
    } catch {
      /* ignore */
    }
  }
}

function productImageSrc(url: string | null): string | null {
  if (!url) {
    return null;
  }
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  return `${apiBaseURL.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
}

export function PosScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const token = useAuthStore((s) => s.accessToken);
  const { branchesEnabled } = useBranchesEnabled();
  const { data: branches } = useBranches();
  const [branchId, setBranchId] = useState<number | null>(null);

  useEffect(() => {
    if (!branchesEnabled) {
      if (branches?.length) {
        const prefer =
          user?.branch_id != null
            ? branches.find((b) => Number(b.id) === Number(user.branch_id))
            : undefined;
        const pick = prefer ?? branches[0];
        setBranchId(Number(pick.id));
      } else if (user?.branch_id != null && user.branch_id > 0) {
        setBranchId(user.branch_id);
      }
      return;
    }
    if (branchId !== null) {
      return;
    }
    if (branches?.length) {
      const prefer =
        user?.branch_id != null
          ? branches.find((b) => Number(b.id) === Number(user.branch_id))
          : undefined;
      const pick = prefer ?? branches[0];
      setBranchId(Number(pick.id));

      return;
    }
    if (user?.branch_id != null && user.branch_id > 0) {
      setBranchId(user.branch_id);
    }
  }, [branches, branchId, user?.branch_id, branchesEnabled]);

  const [searchInput, setSearchInput] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput), 280);

    return () => clearTimeout(t);
  }, [searchInput]);

  const [categoryId, setCategoryId] = useState<number | undefined>();
  const { data: catTree } = useCategoryTree();
  const categories = useMemo(() => flattenCategories(catTree), [catTree]);

  const { data: prodData, isFetching } = useProducts({
    page: 1,
    per_page: 120,
    search: searchDebounced || undefined,
    category_id: categoryId,
    branch_id: branchesEnabled ? (branchId ?? undefined) : undefined,
    stock_status: "all",
    enabled: token != null,
  });

  const showBranchPicker = branchesEnabled && (branches?.length ?? 0) > 1;

  const saleBranchReady =
    !branchesEnabled || branchId != null || (branches?.length ?? 0) > 0;

  // Refetch products on window focus so stock edits in the Products tab
  // become visible in POS without manual reload.
  useEffect(() => {
    const onFocus = () => {
      void qc.invalidateQueries({ queryKey: ["products"] });
    };
    window.addEventListener("focus", onFocus);

    return () => window.removeEventListener("focus", onFocus);
  }, [qc]);

  const products = useMemo(() => prodData?.data ?? [], [prodData?.data]);

  const [view, setView] = useState<"list" | "grid">("list");
  const [cart, setCart] = useState<CartLine[]>([]);
  const [cartDiscPct, setCartDiscPct] = useState(0);
  const [customer, setCustomer] = useState<CustomerRow | null>(null);
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const customerRef = useRef(customer);
  customerRef.current = customer;
  const cartDiscPctRef = useRef(cartDiscPct);
  cartDiscPctRef.current = cartDiscPct;
  const [custQuery, setCustQuery] = useState("");
  const [custHits, setCustHits] = useState<CustomerRow[]>([]);
  const [custMobile, setCustMobile] = useState("");
  const [custName, setCustName] = useState("");
  const [custEmail, setCustEmail] = useState("");
  const [custGstin, setCustGstin] = useState("");
  const [custAddress, setCustAddress] = useState("");
  const [showMoreCust, setShowMoreCust] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const mobileDigits = useMemo(() => custMobile.replace(/\D/g, ""), [custMobile]);
  const mobileValid = mobileDigits.length >= 10;
  const [payOpen, setPayOpen] = useState(false);
  const [applicableOffers, setApplicableOffers] = useState<ApplicableOffer[]>([]);
  const [offersBusy, setOffersBusy] = useState(false);
  const [successMeta, setSuccessMeta] = useState<{
    id: number;
    bill_no: string;
    customer: CustomerRow | null;
    public_invoice_url?: string | null;
    public_invoice_portal_url?: string | null;
    public_invoice_token?: string | null;
  } | null>(null);
  const [holdOpen, setHoldOpen] = useState(false);
  const [, bumpHoldList] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  const envMerchantUpi = (import.meta.env.VITE_MERCHANT_UPI ?? null) as string | null;
  const [merchantUpi, setMerchantUpi] = useState<string | null>(envMerchantUpi);

  const loadMerchantUpi = useCallback(() => {
    void api
      .get<{ settings: { key: string; value: string | null }[] }>("/api/settings", {
        params: { group: "integrations" },
      })
      .then((r) => {
        const rows = r.data.settings ?? [];
        const row = rows.find((x) => x.key === "merchant_upi");
        const v = row?.value ? String(row.value).trim() : "";
        setMerchantUpi(v !== "" ? v : envMerchantUpi);
      })
      .catch(() => {
        // keep env fallback
        setMerchantUpi(envMerchantUpi);
      });
  }, [envMerchantUpi]);

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    loadMerchantUpi();
    const onFocus = () => loadMerchantUpi();
    window.addEventListener("focus", onFocus);

    return () => window.removeEventListener("focus", onFocus);
  }, [loadMerchantUpi]);

  const branchRow = useMemo(() => {
    const id = branchId ?? branches?.[0]?.id ?? null;
    const b = id != null ? branches?.find((x) => x.id === id) : undefined;

    return { gstin: b?.gstin ?? "", state_code: (b as { state_code?: string } | undefined)?.state_code };
  }, [branches, branchId]);

  useEffect(() => {
    if (cart.length === 0) {
      setApplicableOffers([]);

      return;
    }
    const t = setTimeout(() => {
      setOffersBusy(true);
      void api
        .post<{ applicable_offers: ApplicableOffer[] }>("/api/offers/check", {
          items: cart.map((c) => ({
            product_id: c.product.id,
            category_id: c.product.category_id ?? undefined,
            qty: c.qty,
            line_total: c.rate * c.qty,
          })),
        })
        .then((r) => setApplicableOffers(r.data.applicable_offers ?? []))
        .catch(() => setApplicableOffers([]))
        .finally(() => setOffersBusy(false));
    }, 280);

    return () => clearTimeout(t);
  }, [cart]);

  const lineOffers = useMemo(() => {
    const m = new Map<number, LineOfferResolution>();
    for (const c of cart) {
      m.set(c.product.id, resolveLineOffer(c, applicableOffers));
    }

    return m;
  }, [cart, applicableOffers]);

  const cartBillTotalQty = useMemo(() => cartBillQtyTotal(cart, lineOffers), [cart, lineOffers]);

  const gst = useMemo(() => {
    const lines = cart.map((c) => {
      const o = lineOffers.get(c.product.id);
      const billQty = o?.billQty ?? c.qty;
      const offerPct = o?.offerDiscPct ?? 0;

      return {
        rate: c.rate,
        qty: billQty,
        disc_pct: effectiveLineDiscPct(c.discPct, offerPct),
        gst_rate: c.product.gst_rate,
      };
    });

    return calculateGst(lines, customer, branchRow, cartDiscPct);
  }, [cart, customer, branchRow, cartDiscPct, lineOffers]);

  const summary = gst.summary;

  useEffect(() => {
    if (!custQuery.trim()) {
      setCustHits([]);

      return;
    }
    const t = setTimeout(() => {
      void api
        .get<{ customers: CustomerRow[] }>("/api/customers", {
          params: { search: custQuery.trim(), limit: 15 },
        })
        .then((r) => setCustHits(r.data.customers ?? []))
        .catch(() => setCustHits([]));
    }, 250);

    return () => clearTimeout(t);
  }, [custQuery]);

  const addToCart = useCallback((p: ProductRow) => {
    setCart((prev) => {
      const i = prev.findIndex((x) => x.product.id === p.id);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], qty: next[i].qty + 1 };

        return next;
      }

      return [
        ...prev,
        { product: p, qty: 1, rate: p.selling_price, discPct: 0 },
      ];
    });
  }, []);

  const handleBarcode = useCallback(
    (code: string) => {
      const trimmed = code.trim();
      if (!trimmed) {
        return;
      }
      const byBarcode = products.find(
        (p) => p.barcode && p.barcode.toLowerCase() === trimmed.toLowerCase(),
      );
      if (byBarcode) {
        addToCart(byBarcode);
        setSearchInput("");

        return;
      }
      void api
        .get<{ product: ProductRow }>(`/api/products/barcode/${encodeURIComponent(trimmed)}`)
        .then((res) => {
          if (res.data.product) {
            addToCart(res.data.product);
            setSearchInput("");
          }
        })
        .catch(() => {
          /* not found */
        });
    },
    [products, addToCart],
  );

  /** Window-level scanner: fast keys + Enter, ignore typing fields. */
  useEffect(() => {
    if (payOpen || successMeta || holdOpen) {
      return;
    }
    let buf = "";
    let last = 0;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || (e.target as HTMLElement).isContentEditable) {
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }
      const now = Date.now();
      if (now - last > 45) {
        buf = "";
      }
      last = now;
      if (e.key === "Enter") {
        if (buf.length >= 4) {
          handleBarcode(buf);
          playScanBeep();
          e.preventDefault();
        }
        buf = "";

        return;
      }
      if (e.key.length === 1) {
        buf += e.key;
      }
    };
    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [handleBarcode, payOpen, successMeta, holdOpen]);

  const cartLenRef = useRef(0);
  cartLenRef.current = cart.length;
  const mobileValidRef = useRef(false);
  mobileValidRef.current = mobileValid;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPayOpen(false);
        setHoldOpen(false);
        setSuccessMeta(null);

        return;
      }
      if (e.key === "F1") {
        e.preventDefault();
        newBill();
      }
      if (e.key === "F2") {
        e.preventDefault();
        holdBill();
      }
      if (e.key === "F3") {
        e.preventDefault();
        setHoldOpen(true);
      }
      if (e.key === "F4") {
        e.preventDefault();
        if (cartLenRef.current && mobileValidRef.current) {
          setPayOpen(true);
        }
      }
      if (e.key === "F8") {
        e.preventDefault();
        window.alert("Sale return: wire route when backend ready.");
      }
    };
    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- F-keys global; holdBill/newBill stable via refs
  }, []);

  function newBill() {
    setCart([]);
    setCustomer(null);
    setCartDiscPct(0);
    setCustQuery("");
    setCustMobile("");
    setCustName("");
    setCustEmail("");
    setCustGstin("");
    setCustAddress("");
    setShowMoreCust(false);
    setSearchInput("");
    searchRef.current?.focus();
  }

  function holdBill() {
    const c = cartRef.current;
    if (!c.length) {
      return;
    }
    const raw = localStorage.getItem(POS_HOLDS_STORAGE_KEY);
    const list: HeldBill[] = raw ? (JSON.parse(raw) as HeldBill[]) : [];
    const entry: HeldBill = {
      id: crypto.randomUUID(),
      savedAt: Date.now(),
      cart: JSON.parse(JSON.stringify(c)) as CartLine[],
      customer: customerRef.current,
      cartDiscPct: cartDiscPctRef.current,
    };
    const next = [...list, entry].slice(-MAX_HOLDS);
    localStorage.setItem(POS_HOLDS_STORAGE_KEY, JSON.stringify(next));
    bumpHoldList((v) => v + 1);
    newBill();
  }

  function loadHolds(): HeldBill[] {
    try {
      const raw = localStorage.getItem(POS_HOLDS_STORAGE_KEY);

      return raw ? (JSON.parse(raw) as HeldBill[]) : [];
    } catch {
      return [];
    }
  }

  function resumeHold(h: HeldBill) {
    setCart(h.cart);
    setCustomer(h.customer);
    setCartDiscPct(h.cartDiscPct);
    if (h.customer) {
      setCustMobile(h.customer.phone);
      setCustName(h.customer.name);
      setCustEmail(h.customer.email ?? "");
      setCustGstin(h.customer.gstin ?? "");
      setCustAddress(h.customer.address ?? "");
      setCustQuery(`${h.customer.name} (${h.customer.phone})`);
    } else {
      setCustMobile("");
      setCustName("");
      setCustEmail("");
      setCustGstin("");
      setCustAddress("");
      setCustQuery("");
    }
    setHoldOpen(false);
  }

  async function applyCustomerLookup() {
    if (!mobileValid) {
      window.alert("Mobile required (min 10 digits)");

      return;
    }
    setLookupBusy(true);
    try {
      const { data } = await api.post<{ customer: CustomerRow }>("/api/customers/lookup-or-create", {
        phone: custMobile,
        name: custName.trim() || undefined,
        email: custEmail.trim() || undefined,
        gstin: custGstin.trim() || undefined,
        address: custAddress.trim() || undefined,
      });
      setCustomer(data.customer);
      setCustName(data.customer.name);
      setCustEmail(data.customer.email ?? "");
      setCustGstin(data.customer.gstin ?? "");
      setCustAddress(data.customer.address ?? "");
      setCustQuery(`${data.customer.name} (${data.customer.phone})`);
      setCustHits([]);
    } catch (err) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? String((err as { response?: { data?: { error?: string } } }).response?.data?.error)
          : err instanceof Error
            ? err.message
            : "Customer lookup failed";
      window.alert(msg || "Customer lookup failed");
    } finally {
      setLookupBusy(false);
    }
  }

  function deleteHold(id: string) {
    const list = loadHolds().filter((h) => h.id !== id);
    localStorage.setItem(POS_HOLDS_STORAGE_KEY, JSON.stringify(list));
    bumpHoldList((v) => v + 1);
  }

  const holds = loadHolds();

  async function submitSale(rows: PaymentRow[], notes?: string) {
    const effectiveBranchId = branchesEnabled
      ? (branchId ?? branches?.[0]?.id ?? null)
      : (branchId ?? branches?.[0]?.id ?? 0);
    if (branchesEnabled && !effectiveBranchId) {
      throw new Error("Pick branch");
    }
    if (!mobileValid) {
      throw new Error("Mobile number required (min 10 digits)");
    }
    if (offersBusy && cart.length > 0) {
      throw new Error("Offers still loading — wait a moment and try again");
    }

    let applicable = applicableOffers;
    try {
      const offerRes = await api.post<{ applicable_offers: ApplicableOffer[] }>("/api/offers/check", {
        items: cart.map((c) => ({
          product_id: c.product.id,
          category_id: c.product.category_id ?? undefined,
          qty: c.qty,
          line_total: c.rate * c.qty,
        })),
      });
      applicable = offerRes.data.applicable_offers ?? [];
    } catch {
      /* use last applicableOffers */
    }

    const freshLineOffers = new Map<number, ReturnType<typeof resolveLineOffer>>();
    for (const c of cart) {
      freshLineOffers.set(c.product.id, resolveLineOffer(c, applicable));
    }

    const expectedGst = calculateGst(
      cart.map((c) => {
        const o = freshLineOffers.get(c.product.id);
        const billQty = o?.billQty ?? c.qty;
        const offerPct = o?.offerDiscPct ?? 0;

        return {
          rate: c.rate,
          qty: billQty,
          disc_pct: effectiveLineDiscPct(c.discPct, offerPct),
          gst_rate: c.product.gst_rate,
        };
      }),
      customer,
      branchRow,
      cartDiscPct,
    );
    const expectedNet = expectedGst.summary.net_amount;

    let normalizedPayments = rows;
    if (rows.length === 1) {
      normalizedPayments = [{ ...rows[0], amount: expectedNet }];
    } else {
      const paySum = rows.reduce((a, r) => a + r.amount, 0);
      if (paySum > 0 && Math.abs(paySum - expectedNet) > 0.05) {
        let allocated = 0;
        normalizedPayments = rows.map((r, i) => {
          if (i === rows.length - 1) {
            return { ...r, amount: Math.round((expectedNet - allocated) * 100) / 100 };
          }
          const amt = Math.round((r.amount / paySum) * expectedNet * 100) / 100;
          allocated += amt;

          return { ...r, amount: amt };
        });
      }
    }

    let cust = customer;
    if (!cust) {
      const { data } = await api.post<{ customer: CustomerRow }>("/api/customers/lookup-or-create", {
        phone: custMobile,
        name: custName.trim() || undefined,
        email: custEmail.trim() || undefined,
        gstin: custGstin.trim() || undefined,
        address: custAddress.trim() || undefined,
      });
      cust = data.customer;
      setCustomer(cust);
    } else if (
      custEmail.trim() !== (cust.email ?? "") ||
      custGstin.trim() !== (cust.gstin ?? "") ||
      custAddress.trim() !== (cust.address ?? "") ||
      custName.trim() !== cust.name
    ) {
      try {
        const { data } = await api.post<{ customer: CustomerRow }>("/api/customers/lookup-or-create", {
          phone: cust.phone,
          name: custName.trim() || undefined,
          email: custEmail.trim() || undefined,
          gstin: custGstin.trim() || undefined,
          address: custAddress.trim() || undefined,
        });
        cust = data.customer;
        setCustomer(cust);
      } catch {
        /* keep existing cust */
      }
    }

    const { data } = await api.post<{
      sale: {
        id: number;
        bill_no: string;
        public_invoice_url?: string | null;
        public_invoice_portal_url?: string | null;
        public_invoice_token?: string | null;
      };
    }>("/api/sales", {
      branch_id: effectiveBranchId,
      customer_id: cust?.id ?? null,
      items: cart.map((c) => ({
        product_id: c.product.id,
        qty: c.qty,
        rate: c.rate,
        discount_pct: c.discPct,
        paid_qty: c.qty,
      })),
      payments: normalizedPayments,
      discount_pct: cartDiscPct,
      notes: notes?.trim() ?? "",
    });
    const meta = {
      id: data.sale.id,
      bill_no: data.sale.bill_no,
      customer: cust
        ? {
            id: cust.id,
            name: cust.name,
            phone: cust.phone,
            email: cust.email ?? null,
            gstin: cust.gstin ?? null,
            address: cust.address ?? null,
          }
        : null,
      public_invoice_url: data.sale.public_invoice_url ?? null,
      public_invoice_portal_url: data.sale.public_invoice_portal_url ?? null,
      public_invoice_token: data.sale.public_invoice_token ?? null,
    };
    // Stock changed → refresh product lists everywhere.
    void qc.invalidateQueries({ queryKey: ["products"] });
    newBill();

    return meta;
  }

  const billText = useMemo(() => {
    if (!successMeta) {
      return "";
    }

    const u = resolveInvoicePortalUrl(successMeta)?.trim();
    if (u) {
      return `Bill ${successMeta.bill_no}\n${u}`;
    }

    return `Bill ${successMeta.bill_no}`;
  }, [successMeta]);

  if (!token) {
    return null;
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <header className="flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2 pt-safe sm:gap-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Back"
          onClick={() => navigate("/dashboard")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="font-semibold">POS</div>
        {cart.length > 0 && (
          <Badge variant="outline" className="text-xs font-normal tabular-nums">
            {cartBillTotalQty} item{cartBillTotalQty === 1 ? "" : "s"}
          </Badge>
        )}
        {showBranchPicker ? (
          <select
            className="ml-auto max-w-[200px] rounded-md border border-input bg-background px-2 py-1 text-sm"
            value={branchId ?? ""}
            onChange={(e) => setBranchId(Number(e.target.value) || null)}
          >
            {(branches ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        ) : (
          <div className="ml-auto" aria-hidden />
        )}
        <Button variant="outline" size="sm" onClick={holdBill}>
          Hold (F2)
        </Button>
        <Button variant="outline" size="sm" onClick={() => setHoldOpen(true)}>
          Resume (F3)
        </Button>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 lg:grid-cols-[minmax(0,65%)_minmax(0,35%)]">
        <div className="flex min-h-0 flex-col border-b lg:border-b-0 lg:border-r">
          <div className="flex flex-wrap items-center gap-2 border-b p-2">
            <Input
              ref={searchRef}
              className="max-w-md flex-1"
              placeholder="Search or scan barcode…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const q = searchInput.trim();
                  if (q.length >= 4 && !q.includes(" ")) {
                    handleBarcode(q);
                    playScanBeep();
                  }
                }
              }}
            />
            <div className="flex gap-1">
              <Button
                type="button"
                size="icon"
                variant={view === "list" ? "default" : "outline"}
                onClick={() => setView("list")}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant={view === "grid" ? "default" : "outline"}
                onClick={() => setView("grid")}
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <ScrollArea className="min-h-0 flex-1">
            <div className="flex gap-2 overflow-x-auto p-2 pb-0">
              <Button
                type="button"
                size="sm"
                variant={categoryId === undefined ? "default" : "secondary"}
                className="shrink-0 rounded-full"
                onClick={() => setCategoryId(undefined)}
              >
                All
              </Button>
              {categories.map((c) => (
                <Button
                  key={c.id}
                  type="button"
                  size="sm"
                  variant={categoryId === c.id ? "default" : "secondary"}
                  className="shrink-0 rounded-full"
                  onClick={() => setCategoryId(c.id)}
                >
                  {c.name}
                </Button>
              ))}
            </div>
            <div className="p-2">
              {isFetching && <p className="text-sm text-muted-foreground">Loading…</p>}
              {!isFetching && view === "list" && (
                <div className="space-y-1">
                  {products.map((p) => {
                    const src = productImageSrc(p.image_url);

                    return (
                      <button
                        key={p.id}
                        type="button"
                        className="flex w-full items-center gap-3 rounded-lg border border-transparent px-2 py-2 text-left hover:border-border hover:bg-muted/40"
                        onClick={() => addToCart(p)}
                      >
                        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border bg-muted">
                          {src ? (
                            <img
                              src={src}
                              alt=""
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">
                              No img
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{p.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {p.category_name ?? "—"} · ₹{p.selling_price}
                          </div>
                        </div>
                        <Badge variant={p.stock_qty <= 0 ? "destructive" : "secondary"}>
                          {p.stock_qty}
                        </Badge>
                      </button>
                    );
                  })}
                </div>
              )}
              {!isFetching && view === "grid" && (
                <div className="grid grid-cols-3 gap-2">
                  {products.map((p) => {
                    const src = productImageSrc(p.image_url);

                    return (
                      <button
                        key={p.id}
                        type="button"
                        className="relative aspect-square overflow-hidden rounded-lg border bg-muted text-left shadow-sm"
                        onClick={() => addToCart(p)}
                      >
                        {src ? (
                          <img src={src} alt="" className="absolute inset-0 h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                            No img
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1">
                          <div className="truncate text-[10px] font-medium text-white">{p.name}</div>
                          <div className="text-[10px] text-emerald-200">₹{p.selling_price}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        <div className="flex min-h-0 flex-col bg-muted/20">
          <div className="space-y-2 border-b p-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Input
                className={cn(
                  "sm:max-w-[160px]",
                  !mobileValid && custMobile.length > 0 && "border-destructive focus-visible:ring-destructive",
                )}
                placeholder="Mobile *"
                inputMode="numeric"
                autoComplete="tel"
                required
                value={custMobile}
                onChange={(e) => {
                  setCustMobile(e.target.value);
                  setCustomer(null);
                }}
              />
              <Input
                className="min-w-0 flex-1"
                placeholder="Name (optional)"
                value={custName}
                onChange={(e) => setCustName(e.target.value)}
              />
              <Button
                type="button"
                variant="secondary"
                disabled={lookupBusy || !mobileValid}
                onClick={() => void applyCustomerLookup()}
              >
                Load / add
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowMoreCust((v) => !v)}
              >
                {showMoreCust ? "Hide details" : "More details"}
              </Button>
              <Button type="button" variant="outline" onClick={newBill}>
                Clear (F1)
              </Button>
            </div>
            {!mobileValid && custMobile.length > 0 && (
              <p className="text-xs text-destructive">Mobile must be 10+ digits.</p>
            )}
            {showMoreCust && (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Input
                  placeholder="Email (optional)"
                  type="email"
                  autoComplete="email"
                  value={custEmail}
                  onChange={(e) => setCustEmail(e.target.value)}
                />
                <Input
                  placeholder="GSTIN (optional)"
                  value={custGstin}
                  onChange={(e) => setCustGstin(e.target.value.toUpperCase())}
                />
                <Input
                  className="sm:col-span-2"
                  placeholder="Address (optional)"
                  value={custAddress}
                  onChange={(e) => setCustAddress(e.target.value)}
                />
              </div>
            )}
            <div className="flex gap-2">
              <div className="relative min-w-0 flex-1">
                <Input
                  placeholder="Search customer name / phone…"
                  value={custQuery}
                  onChange={(e) => {
                    setCustQuery(e.target.value);
                    if (!e.target.value) {
                      setCustomer(null);
                    }
                  }}
                />
                {custHits.length > 0 && (
                  <Card className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-auto p-1 shadow-md">
                    {custHits.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                        onClick={() => {
                          setCustomer(c);
                          setCustMobile(c.phone);
                          setCustName(c.name);
                          setCustEmail(c.email ?? "");
                          setCustGstin(c.gstin ?? "");
                          setCustAddress(c.address ?? "");
                          setCustQuery(`${c.name} (${c.phone})`);
                          setCustHits([]);
                        }}
                      >
                        {c.name} · {c.phone}
                      </button>
                    ))}
                  </Card>
                )}
              </div>
            </div>
          </div>

          <ScrollArea className="min-h-0 flex-1">
            <div className="divide-y p-2">
              {cart.map((line) => {
                const offer = lineOffers.get(line.product.id);
                const billQty = offer?.billQty ?? line.qty;
                const freeQty = offer?.freeQty ?? 0;
                const offerPct = offer?.offerDiscPct ?? 0;
                const effDisc = effectiveLineDiscPct(line.discPct, offerPct);
                const lineNet = line.rate * billQty * (1 - effDisc / 100);

                return (
                <SwipeRow
                  key={line.product.id}
                  onDelete={() =>
                    setCart((c) => c.filter((x) => x.product.id !== line.product.id))
                  }
                >
                  <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{line.product.name}</div>
                      {offer?.offerLabel && (
                        <Badge variant="secondary" className="mt-1 text-[10px] font-normal">
                          {offer.offerLabel}
                        </Badge>
                      )}
                      {freeQty > 0 && (
                        <p className="mt-0.5 text-[10px] text-muted-foreground tabular-nums">
                          Bill qty {billQty} ({line.qty} paid + {freeQty} free)
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          type="button"
                          onClick={() =>
                            setCart((c) =>
                              c.map((x) =>
                                x.product.id === line.product.id
                                  ? { ...x, qty: Math.max(0.001, x.qty - 1) }
                                  : x,
                              ),
                            )
                          }
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <Input
                          className="h-8 w-16 px-1 text-center font-mono text-sm"
                          title="Paid qty"
                          value={String(line.qty)}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value);
                            if (!Number.isFinite(v) || v <= 0) {
                              return;
                            }
                            setCart((c) =>
                              c.map((x) => (x.product.id === line.product.id ? { ...x, qty: v } : x)),
                            );
                          }}
                        />
                        {freeQty > 0 && (
                          <span className="text-[10px] font-medium text-primary tabular-nums">
                            ={billQty}
                          </span>
                        )}
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          type="button"
                          onClick={() =>
                            setCart((c) =>
                              c.map((x) =>
                                x.product.id === line.product.id ? { ...x, qty: x.qty + 1 } : x,
                              ),
                            )
                          }
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      <Input
                        type="number"
                        className="h-8 w-20 font-mono text-sm"
                        value={line.rate}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          if (!Number.isFinite(v)) {
                            return;
                          }
                          setCart((c) =>
                            c.map((x) => (x.product.id === line.product.id ? { ...x, rate: v } : x)),
                          );
                        }}
                      />
                      <Input
                        type="number"
                        className="h-8 w-14 font-mono text-sm"
                        title="Disc %"
                        value={line.discPct}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          if (!Number.isFinite(v)) {
                            return;
                          }
                          setCart((c) =>
                            c.map((x) => (x.product.id === line.product.id ? { ...x, discPct: v } : x)),
                          );
                        }}
                      />
                      <span className="w-20 text-right text-sm font-medium tabular-nums">
                        ₹{lineNet.toFixed(0)}
                      </span>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="text-destructive sm:hidden"
                        onClick={() =>
                          setCart((c) => c.filter((x) => x.product.id !== line.product.id))
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </SwipeRow>
                );
              })}
            </div>
          </ScrollArea>

          <div className="space-y-2 border-t bg-card p-3">
            {applicableOffers.length > 0 && (
              <div className="rounded-md border border-dashed bg-muted/30 px-2 py-1.5 text-xs">
                <p className="font-medium text-foreground">Offers applied</p>
                <ul className="mt-1 space-y-0.5 text-muted-foreground">
                  {applicableOffers.map((a) => (
                    <li key={a.offer.id}>{a.offer.name}</li>
                  ))}
                </ul>
              </div>
            )}
            {offersBusy && cart.length > 0 && applicableOffers.length === 0 && (
              <p className="text-xs text-muted-foreground">Checking offers…</p>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="tabular-nums">₹{summary.subtotal.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-muted-foreground">Cart disc %</span>
              <Input
                type="number"
                className="h-8 w-20 font-mono"
                value={cartDiscPct}
                onChange={(e) => setCartDiscPct(parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Discount</span>
              <span className="tabular-nums">−₹{summary.discount_amount.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Taxable</span>
              <span className="tabular-nums">₹{summary.taxable_amount.toLocaleString("en-IN")}</span>
            </div>
            {summary.igst_amount > 0.001 ? (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">IGST</span>
                <span className="tabular-nums">₹{summary.igst_amount.toLocaleString("en-IN")}</span>
              </div>
            ) : (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">CGST / SGST</span>
                <span className="tabular-nums">
                  ₹{summary.cgst_amount.toLocaleString("en-IN")} | ₹
                  {summary.sgst_amount.toLocaleString("en-IN")}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Round off</span>
              <span className="tabular-nums">₹{summary.round_off.toFixed(2)}</span>
            </div>
            <Separator />
            <div className="flex justify-between text-xl font-bold">
              <span>NET</span>
              <span className="tabular-nums">₹{summary.net_amount.toLocaleString("en-IN")}</span>
            </div>
            <Button
              className="w-full py-6 text-lg font-semibold"
              size="lg"
              disabled={!cart.length || !saleBranchReady || !mobileValid || offersBusy}
              onClick={() => setPayOpen(true)}
            >
              CHARGE (F4)
            </Button>
            {!mobileValid && cart.length > 0 && (
              <p className="text-center text-xs text-muted-foreground">
                Enter customer mobile (10+ digits) to charge.
              </p>
            )}
          </div>
        </div>
      </div>

      <PaymentModal
        open={payOpen}
        onOpenChange={setPayOpen}
        netAmount={summary.net_amount}
        merchantUpi={merchantUpi}
        customerEmail={custEmail}
        onPaid={submitSale}
        onFlowFinished={(meta) => {
          setSuccessMeta(meta);
        }}
      />

      <Dialog open={holdOpen} onOpenChange={setHoldOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Held bills</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {holds.length === 0 && <p className="text-sm text-muted-foreground">No holds.</p>}
            {holds.map((h) => (
              <div key={h.id} className="flex items-center gap-2 rounded border p-2">
                <div className="min-w-0 flex-1 text-sm">
                  <div className="font-medium">{new Date(h.savedAt).toLocaleString()}</div>
                  <div className="text-muted-foreground">{h.cart.length} lines</div>
                </div>
                <Button size="sm" onClick={() => resumeHold(h)}>
                  Load
                </Button>
                <Button size="sm" variant="destructive" onClick={() => deleteHold(h.id)}>
                  Drop
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(successMeta)} onOpenChange={() => setSuccessMeta(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sale saved</DialogTitle>
          </DialogHeader>
          {successMeta && (
            <div className="space-y-3">
              <p className="text-lg font-semibold">{successMeta.bill_no}</p>
              {successMeta.customer?.email ? (
                <p className="rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-700">
                  Invoice emailed to {successMeta.customer.email} (if SMTP configured).
                </p>
              ) : null}
              <div className="flex flex-col gap-2">
                <Button onClick={() => void openSalePdf(successMeta.id)}>Print A4 PDF</Button>
                <Button
                  variant="outline"
                  onClick={() => void downloadSalePdf(successMeta.id, successMeta.bill_no)}
                >
                  Download PDF
                </Button>
                {resolveInvoicePortalUrl(successMeta) ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const link = resolveInvoicePortalUrl(successMeta);
                        if (link) {
                          void navigator.clipboard.writeText(link);
                        }
                      }}
                    >
                      Copy share link
                    </Button>
                    <a
                      className={cn(
                        buttonVariants({ variant: "outline" }),
                        "inline-flex h-9 w-full items-center justify-center rounded-lg",
                      )}
                      href={resolveInvoicePortalUrl(successMeta) ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open share link
                    </a>
                  </>
                ) : null}
                <Button variant="outline" onClick={() => void openSaleThermal(successMeta.id)}>
                  Print thermal (preview)
                </Button>
                {successMeta.customer?.phone && (
                  <a
                    className={cn(buttonVariants({ variant: "outline" }), "inline-flex h-9 w-full items-center justify-center rounded-lg")}
                    href={buildWhatsAppLink(successMeta.customer.phone, billText)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    WhatsApp
                  </a>
                )}
                {successMeta.customer?.email && (
                  <a
                    className={cn(buttonVariants({ variant: "outline" }), "inline-flex h-9 w-full items-center justify-center rounded-lg")}
                    href={`mailto:${successMeta.customer.email}?subject=Invoice%20${encodeURIComponent(successMeta.bill_no)}&body=${encodeURIComponent(billText)}`}
                  >
                    Email
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SwipeRow({
  children,
  onDelete,
}: {
  children: ReactNode;
  onDelete: () => void;
}) {
  const startX = useRef<number | null>(null);
  return (
    <div
      className="touch-pan-y"
      onTouchStart={(e) => {
        startX.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        const sx = startX.current;
        if (sx === null) {
          return;
        }
        const end = e.changedTouches[0]?.clientX ?? sx;
        if (sx - end > 70) {
          onDelete();
        }
        startX.current = null;
      }}
    >
      {children}
    </div>
  );
}
